"use client";
import React from "react";
import {
  BiChevronsLeft,
  BiChevronLeft,
  BiChevronsRight,
  BiChevronRight,
} from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import { _setLimit } from "../../../store/slices/global.slice";

const PaginationComponent = ({
  itemPerPage,
  totalItems,
  paginate,
  currentPage,
  loading,
}) => {
  const { limit } = useSelector((state) => state.global);
  const dispatch = useDispatch();

  // Use itemPerPage prop or fallback to Redux limit, ensure it's a valid number
  const itemsPerPage = Number(itemPerPage) || Number(limit) || 10;
  const total = Number(totalItems) || 0;

  if (loading || !total || total === 0) {
    return null;
  }

  const pageNumbers = [];
  const paginationLimit = [
    { label: "5", value: "5" },
    { label: "10", value: "10" },
    { label: "20", value: "20" },
    { label: "30", value: "30" },
    { label: "50", value: "50" },
  ];
  const changePagination = (data) => {
    paginate(1);
    dispatch(_setLimit(parseInt(data)));
  };

  // Calculate total pages safely
  const totalPages = Math.ceil(total / itemsPerPage);
  if (isNaN(totalPages) || totalPages <= 0) {
    return null;
  }

  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const paginationNumber = () => {
    if (pageNumbers.length <= 5) {
      return pageNumbers;
    } else if (pageNumbers.length >= 5 && currentPage <= 4) {
      return [
        1,
        2,
        3,
        4,
        5,
        "ellipsis",
        pageNumbers[pageNumbers.length - 1],
      ];
    } else if (
      pageNumbers.length >= 5 &&
      currentPage >= pageNumbers[pageNumbers.length - 4]
    ) {
      return [
        1,
        "ellipsis",
        pageNumbers[pageNumbers.length - 5],
        pageNumbers[pageNumbers.length - 4],
        pageNumbers[pageNumbers.length - 3],
        pageNumbers[pageNumbers.length - 2],
        pageNumbers[pageNumbers.length - 1],
      ];
    } else if (
      pageNumbers.length > 5 &&
      currentPage > 4 &&
      currentPage < pageNumbers[pageNumbers.length - 4]
    ) {
      return [
        1,
        "ellipsis",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "ellipsis",
        pageNumbers[pageNumbers.length - 1],
      ];
    }
  };

  let paginationItms = paginationNumber();

  const firstPage = () => {
    paginate(1);
  };

  const lastPage = () => {
    paginate(pageNumbers[pageNumbers.length - 1]);
  };

  const nextPage = () => {
    paginate(currentPage + 1);
  };

  const prevPage = () => {
    paginate(currentPage - 1);
  };

  const from = currentPage === 1 ? 1 : (currentPage - 1) * itemsPerPage + 1;
  const to = currentPage * itemsPerPage > total ? total : currentPage * itemsPerPage;

  const navBtn = (onClick, disabled, children, rounded = "") =>
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`w-7 h-7 flex items-center justify-center text-gray-500 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-150 ${rounded}`}
    >
      {children}
    </button>;

  return (
    <div className="flex items-center justify-between px-4 py-2 bg-white">

      {/* Left — rows per page + count */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <span className="text-[12px] text-gray-400 font-medium">Rows</span>
          <select
            value={limit}
            onChange={(e) => changePagination(e.target.value)}
            className="text-[12px] font-semibold text-gray-700 border border-gray-200 rounded-md px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 cursor-pointer"
          >
            {paginationLimit.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="h-4 w-px bg-gray-200" />

        <span className="text-[12px] text-gray-400">
          Showing{" "}
          <span className="font-semibold text-gray-700">{from}–{to}</span>
          {" "}of{" "}
          <span className="font-semibold text-gray-700">{total}</span>
        </span>
      </div>

      {/* Right — page buttons */}
      <nav className="flex items-center border border-gray-200 rounded-lg overflow-hidden divide-x divide-gray-200" aria-label="Pagination">
        {navBtn(firstPage, currentPage === 1, <BiChevronsLeft className="w-3.5 h-3.5" />, "rounded-l-lg")}
        {navBtn(prevPage, currentPage === 1, <BiChevronLeft className="w-3.5 h-3.5" />)}

        {paginationItms?.map((item, idx) => {
          const isEllipsis = !Number.isFinite(item) || typeof item !== "number";
          const isActive = currentPage === item;
          return (
            <button
              key={idx}
              type="button"
              disabled={isEllipsis || isActive}
              onClick={() => !isEllipsis && paginate(item)}
              className={`w-7 h-7 flex items-center justify-center text-[12px] font-medium transition-colors duration-150 ${
                isActive
                  ? "bg-blue-600 text-white font-semibold"
                  : isEllipsis
                  ? "text-gray-400 cursor-default"
                  : "text-gray-600 hover:bg-blue-50 hover:text-blue-600"
              }`}
            >
              {isEllipsis ? "…" : item}
            </button>
          );
        })}

        {navBtn(nextPage, currentPage === pageNumbers[pageNumbers.length - 1], <BiChevronRight className="w-3.5 h-3.5" />)}
        {navBtn(lastPage, currentPage === pageNumbers[pageNumbers.length - 1], <BiChevronsRight className="w-3.5 h-3.5" />, "rounded-r-lg")}
      </nav>
    </div>
  );
};
export default PaginationComponent;
